import { cn } from '@/utils/cn'
import { type VariantProps, cva } from 'class-variance-authority'
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react'
import { Button, type ButtonProps } from '../common/button'
import { Tooltip, TooltipContent } from '../common/tooltip'
import { ArrowIcon } from '../icons/arrow'
import { HelpIcon } from '../icons/help'

const arrowIconVariants = cva('transition-opacity', {
  variants: {
    active: {
      true: '',
      false: 'opacity-50',
    },
  },
})

const sortButtonVariants = cva(cn('flex items-center', 'rounded-lg'), {
  variants: {
    active: {
      true: 'font-bold text-heading-foreground',
      false: '',
    },
  },
})

export interface TableSortButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sortButtonVariants> {
  asc: boolean
}

interface TableSortTooltipButtonProps extends TableSortButtonProps {
  tooltipContent: ReactNode
}

export const TableSortTooltipButton = forwardRef<HTMLButtonElement, ButtonProps & TableSortTooltipButtonProps>(
  ({ tooltipContent, active, asc, children, className, ...props }, ref) => {
    return (
      <Tooltip asChild>
        <Button
          variant="default"
          size="default"
          className={cn(sortButtonVariants({ active, className }))}
          ref={ref}
          {...props}
        >
          {children}
          <HelpIcon className="ml-1" />
          <div className="ml-1 grid h-4 grid-rows-2 items-center justify-center">
            <ArrowIcon size={16} direction="up" className={arrowIconVariants({ active: active && asc })} />
            <ArrowIcon size={16} direction="down" className={arrowIconVariants({ active: active && !asc })} />
          </div>
        </Button>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    )
  }
)
TableSortTooltipButton.displayName = 'TableSortTooltipButton'

export const TableSortButton = forwardRef<HTMLButtonElement, ButtonProps & TableSortButtonProps>(
  ({ active, asc, children, className, ...props }, ref) => {
    return (
      <Button
        variant="default"
        size="default"
        className={cn(sortButtonVariants({ active, className }))}
        ref={ref}
        {...props}
      >
        {children}
        <div className="ml-1 grid h-4 grid-rows-2 items-center justify-center">
          <ArrowIcon size={16} direction="up" className={arrowIconVariants({ active: active && asc })} />
          <ArrowIcon size={16} direction="down" className={arrowIconVariants({ active: active && !asc })} />
        </div>
      </Button>
    )
  }
)
